import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Ban, Copy, ExternalLink, ImageUp, LockOpen, Mail, PlayCircle, Save } from 'lucide-react';
import { PLAN_FEATURES, PLAN_FEATURE_LABEL, parseBRL, type PlanFeature, type RestaurantStatus } from '@garlic/shared';
import { api, errorMessage, fieldErrors, patch, post, put } from '../lib/api';
import { AUDIT_LABEL, formatBRL, formatDateTime } from '../lib/format';
import { Alert, Badge, Button, Card, CardHeader, EmptyState, Field, Input, PageHeader, Spinner, Textarea, cx } from '../ui/primitives';
import { ReasonDialog, useToast } from '../ui/feedback';
import { StatusBadge } from './shared';
import { SlugField } from './SlugField';

/** Bloqueio por tentativas de login (5 erros em 15 min na conta, ou 30 no mesmo IP). */
type LoginLock = { locked: boolean; failures: number; until: string | null; blockedIps: string[] };

type Detail = {
  id: string; slug: string; name: string; legalName: string | null; document: string | null; status: RestaurantStatus;
  statusReason: string | null; statusChangedAt: string | null; timezone: string; phone: string | null; contactEmail: string | null;
  addressLine: string | null; city: string | null; state: string | null; postalCode: string | null; createdAt: string; url: string; panelUrl: string;
  settings: { primaryColor: string; accentColor: string; logoPath: string | null; googleReviewUrl: string | null } | null;
  subscription: { plan: string; maxTables: number; maxStaff: number; features: PlanFeature[]; monthlyFeeCents: number; notes: string | null } | null;
  slugHistory: { id: string; slug: string; createdAt: string }[];
  memberships: { id: string; status: string; createdAt: string; role: { name: string; key: string | null }; user: { id: string; name: string; email: string; pendingInvite: boolean; disabled: boolean }; loginLock: LoginLock }[];
  integrations: Record<string, { status: string; label: string }>;
};

const TABS = [
  ['dados', 'Dados'],
  ['identidade', 'Identidade visual'],
  ['plano', 'Plano'],
  ['equipe', 'Equipe'],
  ['endereco', 'Endereço'],
  ['auditoria', 'Auditoria'],
] as const;
type Tab = (typeof TABS)[number][0];

export function RestaurantDetail() {
  const { id } = useParams();
  const [tab, setTab] = useState<Tab>('dados');
  const [statusAction, setStatusAction] = useState<RestaurantStatus | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['dev-restaurant', id], queryFn: () => api<Detail>(`/api/dev/restaurants/${id}`) });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['dev-restaurant', id] });
    void qc.invalidateQueries({ queryKey: ['dev-restaurants'] });
  };

  if (q.isPending) return <Spinner />;
  if (q.isError) return <Alert tone="danger">{errorMessage(q.error)}</Alert>;
  const r = q.data;

  const statusCopy: Record<RestaurantStatus, { title: string; label: string; desc: ReactNode; tone: 'primary' | 'danger' }> = {
    ACTIVE: { title: r.status === 'SUSPENDED' ? 'Reativar restaurante' : 'Ativar restaurante', label: r.status === 'SUSPENDED' ? 'Reativar' : 'Ativar', tone: 'primary', desc: 'O cardápio público e o acesso dos funcionários ficam liberados.' },
    SUSPENDED: { title: 'Suspender restaurante', label: 'Suspender', tone: 'danger', desc: <>Todos os funcionários são desconectados na hora e o cardápio público fica indisponível. <strong>Nenhum dado é apagado</strong>; você pode reativar depois.</> },
    ONBOARDING: { title: 'Voltar para implantação', label: 'Confirmar', tone: 'primary', desc: 'O cardápio público mostra "em breve"; os funcionários continuam podendo configurar o restaurante.' },
  };

  return (
    <>
      <PageHeader
        eyebrow={<Link to="/dev/restaurantes" className="inline-flex items-center gap-1 hover:text-ink"><ArrowLeft className="size-3.5" /> Restaurantes</Link>}
        title={r.name}
        description={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <StatusBadge status={r.status} />
            <a href={r.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-garlic-600 hover:underline" title="Cardápio público">{r.url} <ExternalLink className="size-3" /></a>
            <a href={r.panelUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-ink-2 hover:underline" title="Painel administrativo do restaurante">Painel: {r.panelUrl} <ExternalLink className="size-3" /></a>
          </span>
        }
        action={
          <div className="flex flex-wrap gap-2">
            {r.status !== 'ACTIVE' && <Button icon={<PlayCircle className="size-4" />} onClick={() => setStatusAction('ACTIVE')}>{statusCopy.ACTIVE.label}</Button>}
            {r.status !== 'SUSPENDED' && <Button variant="secondary" icon={<Ban className="size-4" />} onClick={() => setStatusAction('SUSPENDED')}>Suspender</Button>}
          </div>
        }
      />
      {r.status === 'SUSPENDED' && r.statusReason && (
        <div className="mb-6"><Alert tone="danger" title="Restaurante suspenso">{r.statusReason}{r.statusChangedAt && ` · desde ${formatDateTime(r.statusChangedAt)}`}</Alert></div>
      )}
      <div role="tablist" aria-label="Seções" className="mb-6 flex gap-1 overflow-x-auto border-b border-line">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={cx('-mb-px shrink-0 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors', tab === key ? 'border-garlic-600 text-garlic-700' : 'border-transparent text-ink-3 hover:text-ink')}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'dados' && (
        <div className="space-y-6">
          <DataTab r={r} onSaved={refresh} />
          <Card>
            <CardHeader title="Integrações" description="Visão técnica, sem credenciais." />
            <ul className="divide-y divide-line text-sm">
              {Object.entries(r.integrations).map(([k, v]) => (
                <li key={k} className="flex items-center justify-between gap-3 px-5 py-3">
                  <span className="font-medium">{k === 'printing' ? 'Impressão' : k === 'mercadoPago' ? 'Mercado Pago' : k}</span>
                  <span className="flex items-center gap-2 text-ink-3">
                    {v.label}
                    <Badge tone={v.status === 'ok' ? 'success' : v.status === 'offline' ? 'danger' : v.status === 'failures' ? 'warning' : 'neutral'} dot>
                      {{ ok: 'Operando', offline: 'Offline', failures: 'Com falhas', not_configured: 'Não configurado', not_available: 'Fase futura' }[v.status] ?? v.status}
                    </Badge>
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}
      {tab === 'identidade' && <BrandTab r={r} onSaved={refresh} />}
      {tab === 'plano' && <PlanTab r={r} onSaved={refresh} />}
      {tab === 'equipe' && <TeamTab r={r} />}
      {tab === 'endereco' && <SlugTab r={r} onSaved={refresh} />}
      {tab === 'auditoria' && <AuditTab id={r.id} />}

      {statusAction && (
        <ReasonDialog
          open
          onClose={() => setStatusAction(null)}
          title={statusCopy[statusAction].title}
          description={statusCopy[statusAction].desc}
          confirmLabel={statusCopy[statusAction].label}
          tone={statusCopy[statusAction].tone}
          onConfirm={async (reason) => {
            try {
              await post(`/api/dev/restaurants/${r.id}/status`, { status: statusAction, reason });
              toast.success('Status atualizado');
              refresh();
            } catch (e) {
              throw new Error(errorMessage(e));
            }
          }}
        />
      )}
    </>
  );
}

/* ───────── Dados ───────── */

function useForm<T extends Record<string, unknown>>(initial: T) {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const set = (k: keyof T) => (v: T[keyof T]) => {
    setValues((s) => ({ ...s, [k]: v }));
    setErrors((e) => ({ ...e, [k as string]: '' }));
  };
  return { values, setValues, errors, setErrors, busy, setBusy, set };
}

function DataTab({ r, onSaved }: { r: Detail; onSaved: () => void }) {
  const toast = useToast();
  const f = useForm({
    name: r.name, legalName: r.legalName ?? '', document: r.document ?? '', phone: r.phone ?? '', contactEmail: r.contactEmail ?? '',
    addressLine: r.addressLine ?? '', city: r.city ?? '', state: r.state ?? '', postalCode: r.postalCode ?? '', timezone: r.timezone,
  });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    f.setBusy(true);
    try {
      await patch(`/api/dev/restaurants/${r.id}`, { ...f.values, contactEmail: f.values.contactEmail || null });
      toast.success('Dados salvos');
      onSaved();
    } catch (err) {
      f.setErrors(fieldErrors(err));
      toast.error(errorMessage(err));
    } finally {
      f.setBusy(false);
    }
  };
  const text = (k: keyof typeof f.values, label: string, props: Record<string, unknown> = {}) => (
    <Field label={label} error={f.errors[k]}>
      {(id) => <Input id={id} value={f.values[k]} invalid={!!f.errors[k]} onChange={(e) => f.set(k)(e.target.value)} {...props} />}
    </Field>
  );
  return (
    <form onSubmit={submit} noValidate>
      <Card>
        <CardHeader title="Dados do estabelecimento" description={`Cadastrado em ${formatDateTime(r.createdAt)}`} />
        <div className="grid gap-4 p-5 sm:grid-cols-2">
          {text('name', 'Nome comercial')}
          {text('legalName', 'Razão social')}
          {text('document', 'CNPJ/CPF', { inputMode: 'numeric' })}
          {text('phone', 'Telefone', { type: 'tel' })}
          {text('contactEmail', 'E-mail de contato', { type: 'email' })}
          {text('timezone', 'Fuso horário (IANA)')}
          {text('addressLine', 'Endereço')}
          {text('city', 'Cidade')}
          {text('state', 'UF', { maxLength: 2 })}
          {text('postalCode', 'CEP', { inputMode: 'numeric' })}
        </div>
        <div className="flex justify-end border-t border-line px-5 py-3">
          <Button type="submit" loading={f.busy} icon={<Save className="size-4" />}>Salvar</Button>
        </div>
      </Card>
    </form>
  );
}

/* ───────── Identidade visual ───────── */

function BrandTab({ r, onSaved }: { r: Detail; onSaved: () => void }) {
  const toast = useToast();
  const s = r.settings!;
  const f = useForm({ primaryColor: s.primaryColor, accentColor: s.accentColor, googleReviewUrl: s.googleReviewUrl ?? '' });
  const [uploading, setUploading] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    f.setBusy(true);
    try {
      await patch(`/api/dev/restaurants/${r.id}`, f.values);
      toast.success('Identidade visual salva');
      onSaved();
    } catch (err) {
      f.setErrors(fieldErrors(err));
      toast.error(errorMessage(err));
    } finally {
      f.setBusy(false);
    }
  };
  const upload = async (file: File) => {
    const body = new FormData();
    body.append('file', file);
    setUploading(true);
    try {
      await api(`/api/dev/restaurants/${r.id}/logo`, { method: 'POST', body });
      toast.success('Logotipo atualizado');
      onSaved();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setUploading(false);
    }
  };
  const color = (k: 'primaryColor' | 'accentColor', label: string) => (
    <Field label={label} error={f.errors[k]}>
      {(id) => (
        <div className="flex gap-2">
          <input type="color" aria-label={`${label} (seletor)`} value={f.values[k]} onChange={(e) => f.set(k)(e.target.value)} className="h-11 w-14 cursor-pointer rounded-xl border border-line bg-white p-1" />
          <Input id={id} value={f.values[k]} invalid={!!f.errors[k]} className="font-mono uppercase" onChange={(e) => f.set(k)(e.target.value)} />
        </div>
      )}
    </Field>
  );
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_20rem]">
      <form onSubmit={submit} noValidate>
        <Card>
          <CardHeader title="Cores e avaliação" />
          <div className="grid gap-4 p-5 sm:grid-cols-2">
            {color('primaryColor', 'Cor principal')}
            {color('accentColor', 'Cor de destaque')}
            <Field className="sm:col-span-2" label="Link oficial de avaliação no Google" error={f.errors.googleReviewUrl} hint="Aberto pelo botão AVALIAR do comprovante de saída.">
              {(id, d) => <Input id={id} aria-describedby={d} type="url" placeholder="https://g.page/r/…/review" value={f.values.googleReviewUrl} invalid={!!f.errors.googleReviewUrl} onChange={(e) => f.set('googleReviewUrl')(e.target.value)} />}
            </Field>
          </div>
          <div className="flex justify-end border-t border-line px-5 py-3">
            <Button type="submit" loading={f.busy} icon={<Save className="size-4" />}>Salvar</Button>
          </div>
        </Card>
      </form>
      <Card>
        <CardHeader title="Logotipo" description="PNG, JPG ou WebP até 2 MB." />
        <div className="space-y-4 p-5">
          <div className="grid aspect-square place-items-center overflow-hidden rounded-2xl border border-dashed border-line bg-paper">
            {s.logoPath ? <img src={s.logoPath} alt={`Logotipo de ${r.name}`} className="size-full object-contain p-4" /> : <span className="text-sm text-ink-3">Sem logotipo</span>}
          </div>
          <label className={cx('flex h-10 cursor-pointer items-center justify-center gap-2 rounded-xl border border-line bg-white text-sm font-medium hover:bg-paper-2', uploading && 'pointer-events-none opacity-60')}>
            <ImageUp className="size-4" /> {uploading ? 'Enviando…' : 'Enviar imagem'}
            <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
          </label>
          <div className="rounded-xl p-4 text-center text-sm font-semibold" style={{ background: f.values.primaryColor, color: '#fff' }}>
            Prévia do botão principal
          </div>
        </div>
      </Card>
    </div>
  );
}

/* ───────── Plano ───────── */

function PlanTab({ r, onSaved }: { r: Detail; onSaved: () => void }) {
  const toast = useToast();
  const s = r.subscription!;
  const f = useForm({ plan: s.plan, maxTables: String(s.maxTables), maxStaff: String(s.maxStaff), features: s.features, fee: (s.monthlyFeeCents / 100).toFixed(2).replace('.', ','), notes: s.notes ?? '' });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const monthlyFeeCents = parseBRL(f.values.fee);
    if (monthlyFeeCents === null) return f.setErrors({ monthlyFeeCents: 'Valor inválido. Ex.: 199,90' });
    f.setBusy(true);
    try {
      await put(`/api/dev/restaurants/${r.id}/subscription`, {
        plan: f.values.plan, maxTables: Number(f.values.maxTables), maxStaff: Number(f.values.maxStaff), features: f.values.features, monthlyFeeCents, notes: f.values.notes,
      });
      toast.success('Plano atualizado');
      onSaved();
    } catch (err) {
      f.setErrors(fieldErrors(err));
      toast.error(errorMessage(err));
    } finally {
      f.setBusy(false);
    }
  };
  const toggle = (feat: PlanFeature) => f.set('features')(f.values.features.includes(feat) ? f.values.features.filter((x) => x !== feat) : [...f.values.features, feat]);
  return (
    <form onSubmit={submit} noValidate>
      <Card>
        <CardHeader title="Plano e limites" description={`Mensalidade atual: ${formatBRL(s.monthlyFeeCents)} — receita do Garlic, separada das vendas do restaurante.`} />
        <div className="grid gap-4 p-5 sm:grid-cols-2">
          <Field label="Nome do plano" error={f.errors.plan}>{(id) => <Input id={id} value={f.values.plan} onChange={(e) => f.set('plan')(e.target.value)} />}</Field>
          <Field label="Mensalidade (R$)" error={f.errors.monthlyFeeCents}>{(id) => <Input id={id} inputMode="decimal" value={f.values.fee} invalid={!!f.errors.monthlyFeeCents} onChange={(e) => f.set('fee')(e.target.value)} />}</Field>
          <Field label="Limite de mesas" error={f.errors.maxTables}>{(id) => <Input id={id} type="number" min={1} value={f.values.maxTables} onChange={(e) => f.set('maxTables')(e.target.value)} />}</Field>
          <Field label="Limite de funcionários" error={f.errors.maxStaff}>{(id) => <Input id={id} type="number" min={1} value={f.values.maxStaff} onChange={(e) => f.set('maxStaff')(e.target.value)} />}</Field>
          <fieldset className="sm:col-span-2">
            <legend className="mb-2 text-sm font-medium text-ink-2">Funcionalidades liberadas</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {PLAN_FEATURES.map((feat) => (
                <label key={feat} className="flex cursor-pointer items-center gap-3 rounded-xl border border-line px-3 py-2.5 text-sm hover:bg-paper">
                  <input type="checkbox" className="size-4 accent-garlic-600" checked={f.values.features.includes(feat)} onChange={() => toggle(feat)} />
                  {PLAN_FEATURE_LABEL[feat]}
                </label>
              ))}
            </div>
          </fieldset>
          <Field className="sm:col-span-2" label="Observações internas">{(id) => <Textarea id={id} value={f.values.notes} onChange={(e) => f.set('notes')(e.target.value)} />}</Field>
        </div>
        <div className="flex justify-end border-t border-line px-5 py-3">
          <Button type="submit" loading={f.busy} icon={<Save className="size-4" />}>Salvar plano</Button>
        </div>
      </Card>
    </form>
  );
}

/* ───────── Equipe ───────── */

function TeamTab({ r }: { r: Detail }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [busyId, setBusyId] = useState<string>();
  const unlock = async (userId: string) => {
    setBusyId(userId);
    try {
      const res = await post<{ ipsUnblocked: string[] }>(`/api/dev/restaurants/${r.id}/members/${userId}/unlock`);
      toast.success(res.ipsUnblocked.length ? 'Login liberado (a rede do restaurante também foi liberada)' : 'Login liberado. A pessoa já pode tentar de novo.');
      await qc.invalidateQueries({ queryKey: ['dev-restaurant', r.id] });
      void qc.invalidateQueries({ queryKey: ['dev-dashboard'] });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusyId(undefined);
    }
  };
  const [link, setLink] = useState<string>();
  const resend = async (userId: string) => {
    setBusyId(userId);
    try {
      const res = await post<{ inviteSentByEmail: boolean; inviteUrl?: string }>(`/api/dev/restaurants/${r.id}/invites`, { userId });
      if (res.inviteUrl) setLink(res.inviteUrl);
      toast.success(res.inviteSentByEmail ? 'Convite reenviado por e-mail' : 'Novo link de convite gerado');
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusyId(undefined);
    }
  };
  return (
    <Card>
      <CardHeader title="Equipe" description="A gestão de funcionários é feita pelo administrador do restaurante. Aqui você reenvia convites e libera logins travados por tentativas erradas." />
      {link && (
        <div className="space-y-2 border-b border-line p-5">
          <Alert tone="warning">E-mail não configurado: repasse este link por um canal seguro (vale 7 dias, uso único).</Alert>
          <div className="flex gap-2">
            <Input readOnly value={link} className="font-mono text-xs" aria-label="Link de convite" onFocus={(e) => e.currentTarget.select()} />
            <Button variant="secondary" icon={<Copy className="size-4" />} onClick={() => navigator.clipboard.writeText(link).then(() => toast.success('Link copiado'))}>Copiar</Button>
          </div>
        </div>
      )}
      {r.memberships.length === 0 ? (
        <EmptyState title="Nenhum funcionário" />
      ) : (
        <ul className="divide-y divide-line">
          {r.memberships.map((m) => (
            <li key={m.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm">
              <div className="min-w-0">
                <p className="font-medium">{m.user.name}</p>
                <p className="truncate text-xs text-ink-3">{m.user.email}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="brand">{m.role.name}</Badge>
                {m.status !== 'ACTIVE' && <Badge tone="danger">{m.status === 'BLOCKED' ? 'Bloqueado' : 'Removido'}</Badge>}
                {m.loginLock.locked && (
                  <>
                    <span title={m.loginLock.blockedIps.length ? `Rede também bloqueada: ${m.loginLock.blockedIps.join(', ')}` : undefined}>
                      <Badge tone="danger">Login travado por tentativas{m.loginLock.until ? ` até ${formatDateTime(m.loginLock.until)}` : ''}</Badge>
                    </span>
                    <Button variant="secondary" size="sm" loading={busyId === m.user.id} icon={<LockOpen className="size-3.5" />} onClick={() => unlock(m.user.id)}>Desbloquear</Button>
                  </>
                )}
                {m.user.pendingInvite && (
                  <>
                    <Badge tone="warning">Convite pendente</Badge>
                    <Button variant="ghost" size="sm" loading={busyId === m.user.id} icon={<Mail className="size-3.5" />} onClick={() => resend(m.user.id)}>Reenviar</Button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/* ───────── Endereço ───────── */

function SlugTab({ r, onSaved }: { r: Detail; onSaved: () => void }) {
  const toast = useToast();
  const [slug, setSlug] = useState(r.slug);
  const [available, setAvailable] = useState(false);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => setSlug(r.slug), [r.slug]);
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader title="Alterar endereço" description="O endereço antigo continua redirecionando para o novo e fica reservado para este restaurante." />
        <div className="space-y-4 p-5">
          <SlugField value={slug} onChange={setSlug} exceptId={r.id} onAvailability={setAvailable} />
          <Alert tone="warning">QR Codes já impressos continuarão funcionando pelo redirecionamento, mas recomendamos reimprimi-los.</Alert>
          <div className="flex justify-end">
            <Button disabled={!available || slug === r.slug} onClick={() => setConfirm(true)}>Alterar endereço</Button>
          </div>
        </div>
      </Card>
      <Card>
        <CardHeader title="Endereços anteriores" />
        {r.slugHistory.length === 0 ? (
          <EmptyState title="Nenhuma alteração de endereço" />
        ) : (
          <ul className="divide-y divide-line text-sm">
            {r.slugHistory.map((h) => (
              <li key={h.id} className="flex justify-between gap-3 px-5 py-3">
                <span className="font-mono">{h.slug}</span>
                <span className="text-ink-3">até {formatDateTime(h.createdAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <ReasonDialog
        open={confirm}
        onClose={() => setConfirm(false)}
        title="Confirmar novo endereço"
        description={<>O restaurante passará a responder em <strong className="font-mono">/r/{slug}</strong> e <strong className="font-mono">/painel/{slug}</strong>. Links e QR Codes com o endereço antigo continuam funcionando (redirecionam), e ninguém precisa entrar de novo.</>}
        confirmLabel="Alterar"
        onConfirm={async (reason) => {
          try {
            await post(`/api/dev/restaurants/${r.id}/slug`, { slug, reason });
            toast.success('Endereço alterado');
            onSaved();
          } catch (e) {
            throw new Error(errorMessage(e));
          }
        }}
      />
    </div>
  );
}

/* ───────── Auditoria ───────── */

type Log = { id: string; action: string; actorType: string; actorLabel: string | null; data: unknown; ip: string | null; createdAt: string };

function AuditTab({ id }: { id: string }) {
  const q = useQuery({ queryKey: ['dev-restaurant-audit', id], queryFn: () => api<Log[]>(`/api/dev/restaurants/${id}/audit`) });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <Alert tone="danger">Não foi possível carregar a auditoria.</Alert>;
  return <AuditList logs={q.data} />;
}

export function AuditList({ logs, showRestaurant }: { logs: (Log & { restaurant?: { name: string } | null })[]; showRestaurant?: boolean }) {
  if (logs.length === 0) return <Card><EmptyState title="Nenhum evento registrado" /></Card>;
  return (
    <Card>
      <ul className="divide-y divide-line">
        {logs.map((l) => (
          <li key={l.id} className="px-5 py-3 text-sm">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="font-medium">{AUDIT_LABEL[l.action] ?? l.action}</p>
              <time className="text-xs text-ink-3" dateTime={l.createdAt}>{formatDateTime(l.createdAt)}</time>
            </div>
            <p className="text-xs text-ink-3">
              {showRestaurant && l.restaurant ? `${l.restaurant.name} · ` : ''}
              {l.actorLabel ?? (l.actorType === 'SYSTEM' ? 'sistema' : l.actorType)}
              {l.ip ? ` · IP ${l.ip}` : ''}
            </p>
            {l.data != null && typeof l.data === 'object' && Object.keys(l.data).length > 0 && (
              <details className="mt-1">
                <summary className="cursor-pointer text-xs text-garlic-600">Detalhes</summary>
                <pre className="mt-1 overflow-x-auto rounded-lg bg-paper-2 p-2 text-xs text-ink-2">{JSON.stringify(l.data, null, 2)}</pre>
              </details>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
